// janetNavigation — where Janet may take a person inside Chatrone (Aaron,
// 2026-10-08: "Janet has to be able to take me anywhere within Chatrone").
//
// PURE — no Supabase import — so the chat endpoint (api/) and the panel
// (src/) read the SAME map. Navigation is read-only (opening a page changes
// nothing), so a valid path is followed straight away; the rule that keeps it
// honest is that a path must be one of the app's own routes, never a URL, and
// a record route must carry a real id (the endpoint checks the record exists
// before the panel ever sees it).

// Pages a person can be taken to by name. Labels are the words on screen.
export const NAMED_PAGES = [
  { path: '/', label: 'Home' },
  { path: '/inbox', label: 'Inbox' },
  { path: '/janet/queue', label: 'Janet Queue' },
  { path: '/janet/attention', label: 'What needs you (Attention Read)' },
  { path: '/janet/chat', label: 'Janet chat' },
  { path: '/janet/knowledge', label: 'What Janet has learned (knowledge review)' },
  { path: '/janet/calibration', label: 'Calibration review' },
  { path: '/messages', label: 'Messages' },
  { path: '/tasks', label: 'Tasks' },
  { path: '/comms', label: 'Comms' },
  { path: '/calendar', label: 'Calendar' },
  { path: '/contacts', label: 'Directory (contacts)' },
  { path: '/companies', label: 'Companies' },
  { path: '/clients', label: 'Clients' },
  { path: '/studio/hub', label: 'Production — the Slate' },
  { path: '/properties', label: 'Properties' },
  { path: '/projects', label: 'Expressions' },
  { path: '/studio/design', label: 'Design takes (every design brief)' },
  { path: '/deals', label: 'Deals' },
  { path: '/opportunities', label: 'Opportunities' },
  { path: '/submissions', label: 'Submissions' },
  { path: '/finance', label: 'Finance' },
  { path: '/research', label: 'Research' },
  { path: '/files', label: 'Files' },
  { path: '/reports', label: 'Reports' },
  { path: '/engineering', label: 'Engineering' },
  { path: '/settings', label: 'Settings' },
  { path: '/activity', label: 'Activity' },
]

// Record routes. `table` names the row the id must exist in (checked by the
// endpoint); null means the id is only shape-checked.
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_RE = new RegExp(`^${UUID}$`, 'i')
export const RECORD_ROUTES = [
  { kind: 'property',   re: new RegExp(`^/properties/(${UUID})$`, 'i'),                table: 'properties' },
  { kind: 'expression', re: new RegExp(`^/projects/(${UUID})$`, 'i'),                  table: 'projects' },
  { kind: 'draft',      re: new RegExp(`^/projects/(${UUID})/drafts/${UUID}$`, 'i'),   table: 'projects' },
  { kind: 'stage',      re: new RegExp(`^/projects/(${UUID})/stage/[a-z0-9_-]{1,60}$`, 'i'), table: 'projects' },
  { kind: 'deal',       re: new RegExp(`^/deals/(${UUID})(?:/full)?$`, 'i'),           table: 'deals' },
  { kind: 'contact',    re: new RegExp(`^/contacts/(${UUID})$`, 'i'),                  table: 'contacts' },
  { kind: 'company',    re: new RegExp(`^/companies/(${UUID})$`, 'i'),                 table: 'companies' },
  { kind: 'client',     re: new RegExp(`^/clients/(${UUID})$`, 'i'),                   table: null },
  { kind: 'design_brief', re: new RegExp(`^/studio/design/(${UUID})$`, 'i'),           table: 'design_briefs' },
  { kind: 'opportunity', re: new RegExp(`^/opportunities/(${UUID})$`, 'i'),            table: 'opportunities' },
  { kind: 'submission', re: new RegExp(`^/submissions/(?:outbound/)?(${UUID})$`, 'i'), table: null },
  { kind: 'task',       re: new RegExp(`^/tasks/(${UUID})$`, 'i'),                     table: 'tasks' },
  { kind: 'invoice',    re: new RegExp(`^/finance/invoices/(${UUID})$`, 'i'),          table: null },
  { kind: 'person',     re: new RegExp(`^/people-of-interest/(${UUID})$`, 'i'),        table: null },
  { kind: 'workflow',   re: new RegExp(`^/workflows/(${UUID})$`, 'i'),                 table: 'workflow_runs' },
]

// Query parameters the app's own deep links use (tabs, sub-sections, the zoom
// ladder's cell/deliverable/node, the property tiles' lens/open/station).
const DRIVE_URL = /^https:\/\/(drive|docs)\.google\.com\//
// `email` carries a specific `/inbox` message's id — a dated-message
// citation (issue #254 finding 3) opens the exact message, not just the
// inbox list, so its value is kept (and independently UUID-validated below)
// rather than stripped the way an unrecognized param would be.
const ALLOWED_PARAMS = new Set(['tab', 'sub', 'section', 'cell', 'deliverable', 'node', 'view', 'lens', 'open', 'station', 'tool', 'topic', 'q', 'email'])
const SAFE_VALUE = /^[A-Za-z0-9 _.:,-]{1,120}$/

/**
 * Checks a path Janet proposes. Returns
 *   { ok: true, path, kind, id, table }  — a route this app serves
 *   { ok: false, reason }                — anything else (a URL, an unknown page)
 */
export function checkNavPath(raw) {
  // Tolerate the same page written as a full Chatrone address, in quotes, or
  // without its leading slash.
  let s = String(raw || '').trim().replace(/^[`'"]+|[`'"]+$/g, '')
  s = s.replace(/^https?:\/\/(?:chatrone-os\.vercel\.app|localhost:\d+)(?=\/|$)/i, '') || (/^https?:\/\/(?:chatrone-os\.vercel\.app|localhost:\d+)\/?$/i.test(String(raw || '').trim()) ? '/' : '')
  if (/^(properties|projects|deals|contacts|companies|clients|studio|opportunities|submissions|tasks|finance|janet|inbox|calendar|messages|research|files|reports|settings|engineering|comms|activity|people-of-interest|workflows)\b/.test(s)) s = `/${s}`
  if (!s) return { ok: false, reason: 'no page was named' }
  // A file that lives only in Google Drive opens there, in a new tab — the
  // one place outside Chatrone a path may point.
  if (DRIVE_URL.test(s) && !/[\s<>"'\\]/.test(s) && s.length <= 600) {
    return { ok: true, path: s, kind: 'drive_file', id: null, table: null, external: true }
  }
  if (!s.startsWith('/') || s.startsWith('//') || /^[a-z]+:/i.test(s) || /[\s<>"'\\]/.test(s) || s.length > 400) {
    return { ok: false, reason: 'that is not a page in Chatrone' }
  }
  const [pathname, query = ''] = s.split('?')
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const params = new URLSearchParams(query)
  const kept = new URLSearchParams()
  for (const [k, v] of params) {
    if (ALLOWED_PARAMS.has(k) && SAFE_VALUE.test(v)) kept.set(k, v)
  }
  const qs = kept.toString()
  const path = qs ? `${clean}?${qs}` : clean

  const named = NAMED_PAGES.find(p => p.path === clean)
  if (named) {
    // /inbox?email=<uuid> opens one specific dated message — treated as a
    // record route (checked against email_intelligence) rather than the
    // generic page, so a citation always lands on the exact message, never
    // just the inbox list. A malformed `email` value must FAIL validation
    // here — it must never degrade into the generic Inbox page (which would
    // silently drop the broken reference while still reporting `ok: true`).
    if (clean === '/inbox' && params.has('email')) {
      const emailId = params.get('email') || ''
      if (!UUID_RE.test(emailId)) return { ok: false, reason: 'that email id is not valid' }
      return { ok: true, path: `/inbox?email=${emailId.toLowerCase()}`, kind: 'email', id: emailId.toLowerCase(), table: 'email_intelligence' }
    }
    return { ok: true, path, kind: 'page', id: null, table: null, label: named.label }
  }
  for (const r of RECORD_ROUTES) {
    const m = clean.match(r.re)
    if (m) return { ok: true, path, kind: r.kind, id: m[1].toLowerCase(), table: r.table }
  }
  return { ok: false, reason: `Chatrone has no page at ${clean}` }
}

// The URL grammar, written for Janet's prompt. One copy, next to the checker.
export function navigationGuide() {
  const pages = NAMED_PAGES.map(p => `  ${p.path} — ${p.label}`).join('\n')
  return `PAGES (by name):
${pages}

RECORDS (ids come from your lookups — never guess one):
  /properties/<property_id> — a property (its tiles: Writing, Visual development, Other materials…)
  /properties/<property_id>?lens=<project_id> — one expression of that property, on its tiles
  /properties/<property_id>?open=promo — the property's own slate promo (presents the IP and every version)
  /properties/<property_id>?open=files — every Drive file filed to the property or its versions, with which copy is current
  /projects/<project_id>?tab=creative&sub=promo — an expression's promo (watch / approve cuts)
  /projects/<project_id>?tab=creative&sub=materials — an expression's written materials
  /projects/<project_id>?tab=creative&sub=design — an expression's design work
  /projects/<project_id>?tab=business — an expression's deals and submissions
  /projects/<project_id>?view=record — an expression's full record
  /projects/<project_id>/drafts/<draft_id> — one draft
  /deals/<deal_id> — a deal's workroom   (/deals/<deal_id>/full — its full record)
  /contacts/<contact_id> · /companies/<company_id> · /clients/<client_id>
  /inbox?email=<email_intelligence_id> — one specific message in the inbox
  /studio/design/<brief_id> — one design brief and its takes
  /opportunities/<id> · /submissions/outbound/<id> · /tasks/<id> · /finance/invoices/<id>
  https://drive.google.com/… or https://docs.google.com/… — a file that lives only in Google Drive (opens in a new tab)`
}

// Promo door for an expression (the one place the promo lives).
export function promoPath(projectId, pieceId) {
  if (!projectId) return null
  return `/projects/${projectId}?tab=creative&sub=promo${pieceId ? `&node=${pieceId}` : ''}`
}

// The property's own slate promo (presents the IP and every version on it).
export function propertyPromoPath(propertyId) {
  return propertyId ? `/properties/${propertyId}?open=promo` : null
}
